'use client';

import { c } from '../../../utils/tailwind-merge';
import { Link } from '../../atoms/Link';
import { ProductListExistingActions } from './ProductListExistingActions';
import type {
  ProductListHeaderProps,
  ProductListHeaderPropsExisting,
  ProductListHeaderPropsNew,
  ProductListHeaderPropsSeeMore,
} from './ProductListHeader.types';
import { ProductListNewActions } from './ProductListNewActions';
import { ProductListSeeMoreLink } from './ProductListSeeMoreLink';

const titleSizeClasses = {
  lg: 'text-3xl md:text-3xl lg:text-5xl font-bold',
  xl: 'text-4xl md:text-4xl lg:text-7xl font-bold',
};

const renderNewActions = (props: ProductListHeaderPropsNew) => {
  const { microcopy, favoriteListModalProps } = props;

  return (
    <ProductListNewActions
      microcopy={microcopy}
      favoriteListModalProps={favoriteListModalProps}
    />
  );
};

const renderExistingActions = (props: ProductListHeaderPropsExisting) => {
  const {
    microcopy,
    onEdit,
    confirmationPopupProps,
    shareListModalProps,
    hideShare,
  } = props;

  return (
    <ProductListExistingActions
      microcopy={microcopy}
      onEdit={onEdit}
      confirmationPopupProps={confirmationPopupProps}
      shareListModalProps={shareListModalProps}
      hideShare={hideShare}
    />
  );
};

const renderSeeMoreActions = ({
  microcopy,
  href,
  name,
}: ProductListHeaderPropsSeeMore) => (
  <ProductListSeeMoreLink
    href={href}
    name={name}
    microcopy={microcopy}
    className="hidden md:flex"
    testId="product-list-header-see-more-link"
  />
);

export const ProductListHeader = (props: ProductListHeaderProps) => {
  const { className = '', size, name, viewModeToggle } = props;

  let href: string | undefined = undefined;
  if ('href' in props && props.href) {
    href = props.href;
  }

  const renderActions = () => {
    switch (props.type) {
      case 'new':
        return renderNewActions(props);
      case 'existing':
        return renderExistingActions(props);
      case 'seeMore':
        return renderSeeMoreActions(props);
      case 'default':
      default:
        return null;
    }
  };

  const header = (color: string) => (
    <h2 className={c(titleSizeClasses[size], color)}>{name}</h2>
  );

  return (
    <header
      className={c(
        'flex w-full flex-col gap-4 md:flex-row md:items-end md:justify-between',
        className
      )}
      data-test="product-list-header"
    >
      <div className="flex items-center justify-between">
        {href ? (
          <Link href={href}>{header('text-blue')}</Link>
        ) : (
          header('text-grey-900')
        )}
        {viewModeToggle && <div className="md:hidden">{viewModeToggle}</div>}
      </div>
      {renderActions()}
    </header>
  );
};

ProductListHeader.displayName = 'ProductListHeader';
